import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import BorderRadiusChange from '../../../../../components/slides/highlighted/common/borderRadiusChange';
import FontAndBackgroundColorPicker, {COLOR_FIELDS} from '../../../../../components/slides/highlighted/common/fontAndBackgroundColorPicker';
import SectionTitle from '../../../../../components/elements/common/SectionTitle';
import {IBorderRadius} from '../../../../../incase-domain/common/borderRadius';
import {ELEMENT} from '../../../../../incase-domain/elements/base/ElementTypes';
import {IPresentationTheme, StaticContentElement, StaticThemeKey} from '../../../../../incase-domain/presentationTheme';
import {getEffectiveStaticTheme, staticElementTypeToThemeKey} from '../../../../../utils/presentationTheme';
import {buildThemeControlElement} from '../buildThemePreviewElementByType';
import {patchThemeStyles} from '../themeStyle';

interface PresentationThemeStaticElementProps {
    presentationTheme: IPresentationTheme;
    setPresentationTheme: React.Dispatch<React.SetStateAction<IPresentationTheme>>;
    contentType: ELEMENT;
}

export default function PresentationThemeStaticElement(props: PresentationThemeStaticElementProps): JSX.Element {
    const {presentationTheme, setPresentationTheme, contentType} = props;
    const {t} = useTranslation();

    const themeKey: StaticThemeKey = staticElementTypeToThemeKey[contentType] ?? 'htmlElement';
    const effectiveTheme = getEffectiveStaticTheme(presentationTheme.elementStyles, themeKey);
    const isImage = themeKey === 'imageElement';
    const imageBorderRadius = isImage ? presentationTheme.elementStyles?.imageElement?.borderRadius : undefined;

    const mockElement = useMemo(
        () => (isImage ? null : buildThemeControlElement(contentType, presentationTheme)),
        [contentType, isImage, presentationTheme]
    );

    const changeHandler = {
        single: (field: string, value: unknown): void => {
            setPresentationTheme((prevTheme) => {
                let patch: Partial<StaticContentElement> | null = null;

                if (field === 'font' && value && typeof value === 'object' && 'color' in value) {
                    patch = {color: (value as {color: string}).color};
                } else if (field === 'fontColor' && typeof value === 'string') {
                    patch = {color: value};
                } else if (field === 'color' && typeof value === 'string') {
                    patch = {backgroundColor: value};
                } else if (field === 'borderRadius') {
                    patch = {borderRadius: value as IBorderRadius};
                }

                if (!patch) return prevTheme;
                return patchThemeStyles(prevTheme, [themeKey], patch);
            });
        },
    };

    const setImageBorderRadius = (borderRadius: IBorderRadius): void => {
        setPresentationTheme((prevTheme) => patchThemeStyles(prevTheme, ['imageElement'], {borderRadius}));
    };

    const showColors = !isImage && mockElement;
    const showBorderRadius = isImage;

    return (
        <div className="l-group--form">
            <SectionTitle label={t('designSidepanel.style')} />
            <div className="u-flex u-flex-col u-gap-3">
                {showColors && (
                    <FontAndBackgroundColorPicker
                        highlightedElement={mockElement}
                        highlightedElementChangeHandler={changeHandler}
                        colorFields={[COLOR_FIELDS.FONT_COLOR, COLOR_FIELDS.BACKGROUND_COLOR]}
                    />
                )}

                {showBorderRadius && (
                    <BorderRadiusChange
                        borderRadius={isImage ? imageBorderRadius : effectiveTheme?.borderRadius}
                        highlightedElementChangeHandler={(value) =>
                            isImage ? setImageBorderRadius(value as IBorderRadius) : changeHandler.single('borderRadius', value)
                        }
                    />
                )}
            </div>
        </div>
    );
}
